import { TextValidatorType } from "anketa-core/base";
import { Group, Section } from "anketa-core/group";
import { Translation } from "anketa-core/i18n";
import { OpenQuestion } from "anketa-core/open_question";
import { OptionsQuestion } from "anketa-core/options_question";
import { TextValidator } from "anketa-core/validation";

export const setupBaramundiGroup = (baramundiScanners: Section, idx = 0) => {
  const baramundiGroup = baramundiScanner(idx);
  baramundiScanners.addChild(baramundiGroup.baramundiScanner);

  function onConfigChange() {
    const name = baramundiGroup.barmundiName.fact.raw;

    if (name && name.toString().trim() !== "") {
      baramundiGroup.barmundiName.removeChangeListener(onConfigChange);
      setupBaramundiGroup(baramundiScanners, idx + 1);
    }
  }

  baramundiGroup.barmundiName.addChangeListener(onConfigChange);
};

export const baramundiScanner = (
  idx: number
): { baramundiScanner: Group; barmundiName: OpenQuestion } => {
  const idPrefix = `baramundi_scanner_${idx}`;

  const baramundiScanner = new Group(idPrefix, [
    new Translation("en", "Unconfigured Baramundi Scanner"),
    new Translation("de", "Unkonfigurierter Baramundi Scanner"),
  ]);

  const name = new OpenQuestion(`${idPrefix}_name`, [
    new Translation("en", "Display name"),
    new Translation("de", "Anzeigename"),
  ]);
  name.validator = new TextValidator();

  name.required = true;
  name.addChangeListener(() => {
    const plainName = name.fact.raw.toString();
    if (plainName.trim() !== "") {
      baramundiScanner.title = [
        new Translation("en", plainName),
        new Translation("de", plainName),
      ];
    } else {
      baramundiScanner.title = [
        new Translation("en", "Unconfigured Baramundi Scanner"),
        new Translation("de", "Unkonfigurierter Baramundi Scanner"),
      ];
    }
  });
  baramundiScanner.addChild(name);

  const baramundiUrl = new OpenQuestion(`${idPrefix}_url`, [
    new Translation("en", "Baramundi Server URL"),
    new Translation("de", "Baramundi-Server-URL"),
  ]);
  baramundiUrl.validator = new TextValidator(TextValidatorType.WebUrl);

  baramundiUrl.required = true;
  baramundiUrl.help = [
    new Translation(
      "en",
      "The full URL to your Baramundi Management Suite server (e.g., https://baramundi.example.com)."
    ),
    new Translation(
      "de",
      "Die vollständige URL zu Ihrem Baramundi Management Suite Server (z.B. https://baramundi.beispiel.de)."
    ),
  ];
  baramundiScanner.addChild(baramundiUrl);

  // Username (required)
  const baramundiUsername = new OpenQuestion(`${idPrefix}_username`, [
    new Translation("en", "API Username"),
    new Translation("de", "API-Benutzername"),
  ]);
  baramundiUsername.validator = new TextValidator();

  baramundiUsername.required = true;
  baramundiUsername.help = [
    new Translation(
      "en",
      "The username of the dedicated API user with read-only permissions."
    ),
    new Translation(
      "de",
      "Der Benutzername des dedizierten API-Benutzers mit Nur-Lesen-Rechten."
    ),
  ];
  baramundiScanner.addChild(baramundiUsername);

  const baramundiPassword = new OpenQuestion(`${idPrefix}_password`, [
    new Translation("en", "API Password"),
    new Translation("de", "API-Passwort"),
  ]);
  baramundiPassword.validator = new TextValidator();

  baramundiPassword.required = true;
  baramundiPassword.readonly = true;
  baramundiPassword.help = [
    new Translation("en", "The password for the API user."),
    new Translation("de", "Das Passwort für den API-Benutzer."),
  ];
  baramundiScanner.addChild(baramundiPassword);

  const baramundiInsecure = new OptionsQuestion(
    `${idPrefix}_insecure`,
    [
      new Translation("en", "Disable Certificate Verification"),
      new Translation("de", "Zertifikatsüberprüfung deaktivieren"),
    ],
    "default.options.yes_no"
  );
  baramundiInsecure.help = [
    new Translation(
      "en",
      "Should SSL/TLS certificate verification be disabled? Only use this for testing with self-signed certificates."
    ),
    new Translation(
      "de",
      "Soll die Überprüfung von SSL/TLS-Zertifikaten deaktiviert werden? Nur für Tests mit selbstsignierten Zertifikaten verwenden."
    ),
  ];
  if (baramundiInsecure.fact.raw === null) {
    baramundiInsecure.setFactValue(1);
  }
  baramundiScanner.addChild(baramundiInsecure);

  return { baramundiScanner, barmundiName: name };
};
